import { z } from 'zod'
import { pageQuerySchema } from '../pagination'
import { idSchema } from '../validators'
import { ENGAGEMENT_STATUSES, MEETING_STATES, REVIEW_STATES, STAGES, THEMES } from '../vocab'
import { staffClientActionSchema } from './client-actions'
import {
  investorParticipationSchema,
  participationEligibilitySchema,
  staffParticipationSchema,
} from './participation'
import { investorQuestionSchema, staffQuestionSchema } from './questions'

/**
 * Engagement cases (DM-018..021, P-ID-003, D-06, P-ID-006). Investors and staff
 * get different DTOs: the investor shapes are per-kind allowlists of released
 * fields, with display names instead of user ids and no workflow states.
 */

const searchText = z
  .string()
  .max(120)
  .transform((v) => v.trim())

export const engagementListQuerySchema = pageQuerySchema.extend({
  /** Which list to read: defaults to `staff` for Fruuit staff, `investor` otherwise. */
  view: z.enum(['investor', 'staff']).optional(),
  status: z.enum(ENGAGEMENT_STATUSES.values).optional(),
  /** Any objective of the case currently at this stage (latest published milestone). */
  stage: z.enum(STAGES.values).optional(),
  theme: z.enum(THEMES.values).optional(),
  issuer_id: idSchema.optional(),
  /** Case-insensitive match on the company name or case title. */
  q: searchText.optional(),
  /** Staff only: limit to cases visible to one client org (R-06). */
  client_org_id: idSchema.optional(),
})
export type EngagementListQuery = z.infer<typeof engagementListQuerySchema>

export const engagementCaseQuerySchema = z.object({
  view: z.enum(['investor', 'staff']).optional(),
})

const issuerRefSchema = z.object({
  id: z.string(),
  name: z.string(),
  sector: z.string(),
  country: z.string(),
})

export const caseProgressSchema = z.object({
  objectives: z.number().int(),
  /** Objectives at S3 or above (UI-013). */
  evidenced: z.number().int(),
  /** Objectives at S4. */
  completed: z.number().int(),
  /** Σ stage levels / (objectives × 4), 0..100, rounded. */
  percent: z.number().int(),
})
export type CaseProgress = z.infer<typeof caseProgressSchema>

/** One row of the investor engagements list: released fields only. */
export const investorEngagementSummarySchema = z.object({
  id: z.string(),
  title: z.string(),
  theme: z.string().nullable(),
  status: z.enum(ENGAGEMENT_STATUSES.values),
  next_action: z.string().nullable(),
  due_date: z.string().nullable(),
  original_start: z.string().nullable(),
  issuer: issuerRefSchema,
  manager_name: z.string(),
  client_specific: z.boolean(),
  progress: caseProgressSchema,
  released_at: z.string(),
})
export type InvestorEngagementSummary = z.infer<typeof investorEngagementSummarySchema>

const personSchema = z.object({ id: z.string(), name: z.string() })

const releasedEngagementSchema = z.object({
  title: z.string().nullable(),
  theme: z.string().nullable(),
  status: z.string().nullable(),
  next_action: z.string().nullable(),
  due_date: z.string().nullable(),
  released_at: z.string(),
  released_by: personSchema.nullable(),
})

/** One row of the staff engagements list: working fields plus the release status. */
export const staffEngagementSummarySchema = z.object({
  id: z.string(),
  title: z.string(),
  theme: z.string().nullable(),
  status: z.enum(ENGAGEMENT_STATUSES.values),
  state: z.enum(REVIEW_STATES.values),
  next_action: z.string(),
  due_date: z.string(),
  original_start: z.string().nullable(),
  issuer: issuerRefSchema,
  manager: personSchema,
  reviewer: personSchema,
  client_org: z.object({ id: z.string(), name: z.string() }).nullable(),
  subscriber_count: z.number().int(),
  released: releasedEngagementSchema.nullable(),
  progress: caseProgressSchema,
  updated_at: z.string(),
  revision: z.number().int(),
})
export type StaffEngagementSummary = z.infer<typeof staffEngagementSummarySchema>

/** A source cited as evidence. Investors only get client-shareable sources (AUTHZ-009). */
export const evidenceRefSchema = z.object({
  id: z.string(),
  title: z.string(),
  source_class: z.string(),
  publication_date: z.string().nullable(),
  url: z.string(),
})
export type EvidenceRef = z.infer<typeof evidenceRefSchema>

const investorMilestoneSchema = z.object({
  id: z.string(),
  stage: z.enum(STAGES.values),
  rationale: z.string(),
  /** What remains unproven / outstanding. */
  limitations: z.string(),
  assessment_date: z.string(),
  published_at: z.string().nullable(),
  version: z.number().int(),
  /** "What changed since the last update": the change reason, or the stage move. */
  what_changed: z.string(),
  evidence: z.array(evidenceRefSchema),
  /** Evidence this caller may not see (internal sources), counted, never listed (INV-006). */
  withheld_evidence_count: z.number().int(),
})

/**
 * Company responsiveness, derived on the server from correspondence (demo brief): outbound
 * follow-up requests versus company responses, measured with the server clock.
 */
export const RESPONSIVENESS_LABELS = {
  responsive: 'Responsive',
  slow: 'Slow',
  unresponsive: 'Unresponsive',
  not_assessed: 'Not yet assessed',
} as const
export const responsivenessSchema = z.object({
  label: z.enum(['responsive', 'slow', 'unresponsive', 'not_assessed']),
  label_text: z.string(),
  last_response_date: z.string().nullable(),
  /** Days the oldest unanswered follow-up has been waiting; null when nothing is waiting. */
  days_awaiting: z.number().int().nullable(),
  unanswered: z.number().int(),
  outbound: z.number().int(),
  responses: z.number().int(),
  /** The rule, for the tooltip. */
  rule: z.string(),
  evaluated_at: z.string(),
})
export type Responsiveness = z.infer<typeof responsivenessSchema>

export const HISTORY_KINDS = ['milestone', 'dialogue', 'meeting', 'assessment'] as const
export const historyItemSchema = z.object({
  id: z.string(),
  kind: z.enum(HISTORY_KINDS),
  date: z.string(),
  title: z.string(),
  detail: z.string().nullable(),
})
export type HistoryItem = z.infer<typeof historyItemSchema>

export const staffHistoryItemSchema = historyItemSchema.extend({
  state: z.string().nullable(),
  /** `internal` = not visible to investors; `released` = investors can see it. */
  visibility: z.enum(['released', 'internal']),
})
export type StaffHistoryItem = z.infer<typeof staffHistoryItemSchema>

export const investorObjectiveSchema = z.object({
  id: z.string(),
  title: z.string(),
  category: z.string().nullable(),
  baseline: z.string().nullable(),
  change_request: z.string().nullable(),
  next_action: z.string().nullable(),
  due_date: z.string().nullable(),
  owner_name: z.string(),
  stage: z.enum(STAGES.values),
  latest_milestone: investorMilestoneSchema.nullable(),
  milestones: z.array(investorMilestoneSchema),
})
export type InvestorObjective = z.infer<typeof investorObjectiveSchema>

export const investorDialogueSchema = z.object({
  id: z.string(),
  event_date: z.string(),
  event_type: z.string(),
  event_type_label: z.string(),
  title: z.string(),
  company_response: z.string(),
  fruuit_judgment: z.string(),
  substantive: z.boolean(),
  next_action: z.string(),
  due_date: z.string(),
  published_at: z.string().nullable(),
})
export type InvestorDialogue = z.infer<typeof investorDialogueSchema>

export const meetingSummaryItemSchema = z.object({
  id: z.string(),
  title: z.string(),
  meeting_type: z.string(),
  starts_at: z.string(),
  ends_at: z.string(),
  /** The organiser's display zone (IANA); instants stay UTC. */
  timezone: z.string(),
  location: z.string(),
  agenda: z.string(),
  /** Investors only ever see `published` and `cancelled` (CAL-003). */
  status: z.enum(MEETING_STATES.values),
  participation_cutoff: z.string(),
  has_published_minutes: z.boolean(),
})
export type MeetingSummaryItem = z.infer<typeof meetingSummaryItemSchema>

export const meetingsSummarySchema = z.object({
  upcoming_count: z.number().int(),
  past_count: z.number().int(),
  next: meetingSummaryItemSchema.nullable(),
  items: z.array(meetingSummaryItemSchema),
})
export type MeetingsSummary = z.infer<typeof meetingsSummarySchema>

/** `GET /engagements/:id` for an investor (INV-015, D-06, P-ID-006). */
export const investorCaseSchema = z.object({
  view: z.literal('investor'),
  id: z.string(),
  title: z.string(),
  theme: z.string().nullable(),
  status: z.enum(ENGAGEMENT_STATUSES.values),
  next_action: z.string().nullable(),
  due_date: z.string().nullable(),
  original_start: z.string().nullable(),
  fruuit_start: z.string().nullable(),
  released_at: z.string(),
  released_by_name: z.string().nullable(),
  issuer: issuerRefSchema,
  manager_name: z.string(),
  reviewer_name: z.string(),
  client_specific: z.boolean(),
  progress: caseProgressSchema,
  objectives: z.array(investorObjectiveSchema),
  dialogues: z.array(investorDialogueSchema),
  meetings: meetingsSummarySchema,
  questions: z.array(investorQuestionSchema),
  participation: z.object({
    requests: z.array(investorParticipationSchema),
    eligibility: participationEligibilitySchema,
  }),
  responsiveness: responsivenessSchema,
  /** One chronological record: milestones, correspondence, meetings, assessment versions. */
  history: z.array(historyItemSchema),
})
export type InvestorCase = z.infer<typeof investorCaseSchema>

const staffMilestoneSchema = investorMilestoneSchema.extend({
  state: z.enum(REVIEW_STATES.values),
  change_reason: z.string().nullable(),
  created_by: personSchema.nullable(),
  updated_at: z.string(),
  revision: z.number().int(),
})

export const staffObjectiveSchema = z.object({
  id: z.string(),
  title: z.string(),
  category: z.string().nullable(),
  baseline: z.string(),
  change_request: z.string(),
  next_action: z.string(),
  due_date: z.string(),
  owner: personSchema,
  state: z.enum(REVIEW_STATES.values),
  released: z
    .object({
      title: z.string().nullable(),
      baseline: z.string().nullable(),
      change_request: z.string().nullable(),
      next_action: z.string().nullable(),
      due_date: z.string().nullable(),
      released_at: z.string(),
    })
    .nullable(),
  stage: z.enum(STAGES.values),
  milestones: z.array(staffMilestoneSchema),
  revision: z.number().int(),
})
export type StaffObjective = z.infer<typeof staffObjectiveSchema>

export const staffDialogueSchema = investorDialogueSchema.extend({
  state: z.enum(REVIEW_STATES.values),
  created_by: personSchema.nullable(),
  updated_at: z.string(),
  revision: z.number().int(),
})
export type StaffDialogue = z.infer<typeof staffDialogueSchema>

/** `GET /engagements/:id` for Fruuit staff: every state, with status badges (GAP-UI-012). */
export const staffCaseSchema = z.object({
  view: z.literal('staff'),
  id: z.string(),
  title: z.string(),
  theme: z.string().nullable(),
  status: z.enum(ENGAGEMENT_STATUSES.values),
  state: z.enum(REVIEW_STATES.values),
  next_action: z.string(),
  due_date: z.string(),
  original_start: z.string().nullable(),
  fruuit_start: z.string().nullable(),
  closure_reason: z.string().nullable(),
  issuer: issuerRefSchema,
  manager: personSchema,
  reviewer: personSchema,
  client_org: z.object({ id: z.string(), name: z.string() }).nullable(),
  subscribers: z.array(z.object({ org_id: z.string(), org_name: z.string(), since: z.string() })),
  /** Active Fruuit managers (reassignment targets). */
  managers: z.array(personSchema),
  released: releasedEngagementSchema.nullable(),
  progress: caseProgressSchema,
  objectives: z.array(staffObjectiveSchema),
  dialogues: z.array(staffDialogueSchema),
  meetings: meetingsSummarySchema,
  questions: z.array(staffQuestionSchema),
  participation: z.array(staffParticipationSchema),
  /** Investors' client actions on this case, read-only (R-12). */
  client_actions: z.array(staffClientActionSchema),
  responsiveness: responsivenessSchema,
  history: z.array(staffHistoryItemSchema),
  updated_at: z.string(),
  revision: z.number().int(),
})
export type StaffCase = z.infer<typeof staffCaseSchema>
